import { IBlock } from '@core/typings';
import { getAncestorCustomListing } from './block';

/**
 * Canvas-only marker classes for whatever owns a listing — the Listing Wrapper,
 * or a column switched to "Listing". The editor stylesheet turns these into a badge on
 * the block so a designer can see at a glance which parts of the layout are
 * driven by a listing — and, more importantly, which ones still need one
 * picked. Never emitted in production: the sent email keeps clean markup.
 */
export function listingScopeCanvasClass<T extends Record<string, any>>(
  attributes: T,
  params: Parameters<NonNullable<IBlock['render']>>[0]
): T {
  if (params.mode === 'production' || params.dataSource?.previewMode) {
    return attributes;
  }

  // The binding is the id — `selectedListing` is only a pre-migration leftover
  // that the pickers now null out on every write. Checking it alone left every
  // scope reading as "unset" the moment id-based binding shipped, so a wrapper
  // with a listing picked kept the "Select a listing" badge and dashed outline.
  //
  // Deliberately the binding, not `resolveBoundListing`: whether a listing has
  // been CHOSEN is the question here. Resolving through the cache would flash
  // the warning back on while the lookup is still in flight.
  const isBound = (value: any) =>
    Boolean(value?.selectedListingId || value?.selectedListing?.id);

  // Inherited counts. A listing column inside a Listing Wrapper renders the
  // wrapper's listing and holds no binding of its own — the owner's pick clears
  // descendant bindings precisely so the owner wins — so judging it on its own
  // value alone flagged "Select a listing" on a column that was already showing
  // one, right below a wrapper labelled with that same listing.
  const hasListing =
    isBound((params.data as any)?.data?.value) ||
    isBound(getAncestorCustomListing(params.context, params.idx)?.data?.value);

  return {
    ...attributes,
    'css-class': [
      attributes['css-class'],
      'bay-listing-scope',
      hasListing ? 'bay-listing-scope--set' : 'bay-listing-scope--unset',
    ]
      .filter(Boolean)
      .join(' '),
  };
}
